/**
 * Peak Hours Heatmap Component
 *
 * Visualizes busiest times by day of week and time slot
 */

import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { PeakHoursData } from "../types/operations-types";
import { MetricTooltip } from "./metric-tooltip";

interface PeakHoursHeatmapProps {
  data: PeakHoursData[];
}

export function PeakHoursHeatmap({ data }: PeakHoursHeatmapProps) {
  const { t } = useTranslation("reports");

  // Get unique time slots and days, sorted
  const timeSlots = Array.from(new Set(data.map((d) => d.timeSlot))).sort();
  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  // Create a map for quick lookups
  const dataMap = new Map<string, PeakHoursData>();
  data.forEach((item) => {
    const key = `${item.dayOfWeek}-${item.timeSlot}`;
    dataMap.set(key, item);
  });

  // Get max utilization for color scaling
  const maxUtilization = Math.max(...data.map((d) => d.utilizationPercentage), 1);

  // Color intensity based on utilization (blue gradient)
  const getColor = (utilization: number | undefined) => {
    if (!utilization) return "bg-muted";
    const intensity = (utilization / maxUtilization) * 100;
    if (intensity >= 80) return "bg-blue-700 text-white";
    if (intensity >= 60) return "bg-blue-600 text-white";
    if (intensity >= 40) return "bg-blue-500 text-white";
    if (intensity >= 20) return "bg-blue-400 text-white";
    return "bg-blue-300 text-gray-900";
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center">
          {t("charts.peakHours.title")}
          <MetricTooltip content={t("charts.peakHours.tooltip")} />
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("charts.peakHours.subtitle")}</p>
      </CardHeader>
      <CardContent>
        {data.length === 0 || timeSlots.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            {t("charts.peakHours.noData")}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Legend */}
            <div className="flex items-center justify-end gap-4 text-[10px] text-muted-foreground">
              <span>{t("charts.peakHours.lessBusy")}</span>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-blue-300" />
                <div className="w-3 h-3 rounded-sm bg-blue-400" />
                <div className="w-3 h-3 rounded-sm bg-blue-500" />
                <div className="w-3 h-3 rounded-sm bg-blue-600" />
                <div className="w-3 h-3 rounded-sm bg-blue-700" />
              </div>
              <span>{t("charts.peakHours.moreBusy")}</span>
            </div>

            {/* Heatmap Grid */}
            <div className="overflow-x-auto">
              <div
                className="inline-grid gap-1"
                style={{ gridTemplateColumns: `80px repeat(${timeSlots.length}, 60px)` }}
              >
                {/* Header row with time slots */}
                <div className="text-[10px] font-medium text-muted-foreground" />
                {timeSlots.map((slot) => (
                  <div
                    key={slot}
                    className="text-[10px] font-medium text-muted-foreground text-center"
                  >
                    {slot.split("-")[0]}
                  </div>
                ))}

                {/* Data rows */}
                {daysOfWeek.map((day) => (
                  <Fragment key={day}>
                    <div className="text-xs font-medium flex items-center">{day.slice(0, 3)}</div>
                    {timeSlots.map((slot) => {
                      const key = `${day}-${slot}`;
                      const item = dataMap.get(key);
                      return (
                        <div
                          key={key}
                          className={cn(
                            "h-8 rounded flex items-center justify-center group relative cursor-pointer transition-all hover:ring-2 hover:ring-primary",
                            getColor(item?.utilizationPercentage)
                          )}
                        >
                          {item && item.appointmentCount > 0 && (
                            <>
                              <span className="text-[10px] font-medium">
                                {item.appointmentCount}
                              </span>
                              {/* Tooltip */}
                              <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 bg-popover text-popover-foreground border rounded-md shadow-md p-2 text-xs whitespace-nowrap">
                                <div className="font-medium">
                                  {day} {slot}
                                </div>
                                <div>
                                  {item.appointmentCount} {t("charts.peakHours.appointments")}
                                </div>
                                <div>
                                  {item.utilizationPercentage.toFixed(1)}%{" "}
                                  {t("charts.peakHours.utilization")}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
